import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, XIcon, CircleIcon } from 'lucide-react';
const ThirdLiquidCulture = () => {
  return <div className="w-full bg-white pt-24" data-id="element-1539">
      <div className="container mx-auto px-4 py-16" data-id="element-1540">
        <div className="max-w-3xl mx-auto" data-id="element-1541">
          {/* Hero Section with rainbow gradient underline */}
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-2" data-id="element-1542">
            The Third Liquid Culture™
          </h1>
          <div className="h-1 w-48 mx-auto bg-gradient-to-r from-green-500 via-amber-500 to-red-500 rounded-full mb-6" data-id="element-1543"></div>
          <h2 className="text-xl md:text-2xl text-center text-gray-600 mb-16" data-id="element-1544">
            The Next Great Culture of Daily Rituals
          </h2>
          <p className="text-lg text-gray-700 mb-10" data-id="element-1545">
            For centuries, human culture has been shaped by two great daily
            beverages.
          </p>
          {/* Three cultures cards with visual progression */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 relative" data-id="element-1546">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-sm transform transition-all duration-300 hover:scale-[1.02]" data-id="element-1547">
              <h3 className="font-bold text-lg mb-3" data-id="element-1548">
                The First Liquid Culture
              </h3>
              <p className="text-gray-700" data-id="element-1549">
                <span className="font-medium" data-id="element-1550">Tea</span>: antioxidants and
                calm, a ritual of balance.
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm transform transition-all duration-300 hover:scale-[1.02]" data-id="element-1551">
              <h3 className="font-bold text-lg mb-3" data-id="element-1552">
                The Second Liquid Culture
              </h3>
              <p className="text-gray-700" data-id="element-1553">
                <span className="font-medium" data-id="element-1554">Coffee</span>: caffeine and
                drive, a ritual of energy.
              </p>
            </div>
            {/* Spora card - slightly larger and more prominent */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-7 rounded-xl shadow-md transform transition-all duration-300 hover:scale-[1.03] border-2 border-red-100" data-id="element-1555">
              <h3 className="font-bold text-lg mb-3" data-id="element-1556">
                The Third Liquid Culture
              </h3>
              <p className="text-gray-700" data-id="element-1557">
                <span className="font-medium" data-id="element-1558">Spora</span>: born from fungi,
                unlocking compounds no bean or leaf can offer.
              </p>
            </div>
          </div>
          {/* What the First Two Delivered - more rhythmic and cultural */}
          <h2 className="text-2xl font-bold mb-6" data-id="element-1559">
            What the First Two Delivered
          </h2>
          <div className="space-y-4 mb-12" data-id="element-1560">
            <p className="text-lg" data-id="element-1561">
              Tea gave us balance. Coffee gave us drive. They have sustained
              humanity's mornings and afternoons for centuries.
            </p>
            <p className="text-lg italic mt-6 border-l-4 border-gray-200 pl-4" data-id="element-1562">
              But both stop short. They only extract what water alone can
              release.
            </p>
          </div>
          {/* What the Third Brings - with bolded benefits */}
          <h2 className="text-2xl font-bold mb-6" data-id="element-1563">What the Third Brings</h2>
          <p className="text-lg mb-6" data-id="element-1564">
            Spora goes further. Every cup delivers:
          </p>
          <div className="space-y-6 mb-12" data-id="element-1565">
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow" data-id="element-1566">
              <h3 className="font-bold mb-2" data-id="element-1567">Antioxidants</h3>
              <p className="text-gray-700" data-id="element-1568">
                → <span className="font-semibold" data-id="element-1569">defend</span> your cells
                against daily stress.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow" data-id="element-1570">
              <h3 className="font-bold mb-2" data-id="element-1571">Immunonutrients</h3>
              <p className="text-gray-700" data-id="element-1572">
                → <span className="font-semibold" data-id="element-1573">support and regulate</span>{' '}
                immune function.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow" data-id="element-1574">
              <h3 className="font-bold mb-2" data-id="element-1575">A Unique Third Pillar</h3>
              <p className="text-gray-700" data-id="element-1576">
                → depending on the mushroom, Spora can also{' '}
                <span className="font-semibold" data-id="element-1577">fuel stamina</span>,{' '}
                <span className="font-semibold" data-id="element-1578">sharpen cognition</span>,{' '}
                <span className="font-semibold" data-id="element-1579">balance metabolism</span>,{' '}
                <span className="font-semibold" data-id="element-1580">reduce inflammation</span>, or{' '}
                <span className="font-semibold" data-id="element-1581">restore calm</span>.
              </p>
            </div>
          </div>
          <p className="text-lg text-gray-700 mb-16" data-id="element-1582">
            This is more than stimulation or balance. It is full-spectrum
            nourishment — a culture that engages multiple systems of the body at
            once.
          </p>
          {/* Comparison Table - visually distinct */}
          <h2 className="text-2xl font-bold mb-6" data-id="element-1583">The Comparison</h2>
          <div className="overflow-x-auto mb-16 bg-gray-50 p-4 rounded-xl shadow-sm" data-id="element-1584">
            <table className="w-full min-w-[600px] bg-white rounded-xl" data-id="element-1585">
              <thead className="bg-gray-100" data-id="element-1586">
                <tr data-id="element-1587">
                  <th className="text-left p-4 border-b border-gray-200 font-bold" data-id="element-1588">
                    Culture
                  </th>
                  <th className="text-left p-4 border-b border-gray-200 font-bold" data-id="element-1589">
                    Primary Effect
                  </th>
                  <th className="text-left p-4 border-b border-gray-200 font-bold" data-id="element-1590">
                    Secondary Effect
                  </th>
                  <th className="text-left p-4 border-b border-gray-200 font-bold" data-id="element-1591">
                    Missing Piece
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1592">
                <tr data-id="element-1593">
                  <td className="p-4 border-b border-gray-100 font-medium" data-id="element-1594">
                    Tea (First)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1595">
                    Antioxidants (balance)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1596">Caffeine</td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1597">
                    Immunonutrients + deeper bioactives
                  </td>
                </tr>
                <tr data-id="element-1598">
                  <td className="p-4 border-b border-gray-100 font-medium" data-id="element-1599">
                    Coffee (Second)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1600">
                    Caffeine (stimulation)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1601">Antioxidants</td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1602">
                    Immune and adaptive support
                  </td>
                </tr>
                <tr className="bg-red-50" data-id="element-1603">
                  <td className="p-4 border-b border-gray-100 font-bold" data-id="element-1604">
                    Spora (Third)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1605">
                    Antioxidants (defense)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1606">
                    Immunonutrients (immune balance)
                  </td>
                  <td className="p-4 border-b border-gray-100" data-id="element-1607">
                    <span className="flex items-center" data-id="element-1608">
                      <span className="font-bold" data-id="element-1609">None</span>.
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Completing the Spectrum - with triangle visualization */}
          <h2 className="text-2xl font-bold mb-6" data-id="element-1610">Completing the Spectrum</h2>
          <div className="flex flex-col md:flex-row gap-8 mb-12 items-center" data-id="element-1611">
            <div className="md:w-2/3" data-id="element-1612">
              <p className="text-lg text-gray-700 mb-4" data-id="element-1613">
                Spora doesn't replace coffee or tea — it completes them. Where
                coffee stimulates and tea balances, Spora fortifies, adapts, and
                restores. It is the evolution of daily ritual, the next cultural
                chapter in how we begin, sustain, and protect our lives.
              </p>
            </div>
            <div className="md:w-1/3 relative h-48 flex items-center justify-center" data-id="element-1614">
              {/* Simple triangle visualization */}
              <div className="relative w-40 h-40" data-id="element-1615">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-green-100 w-16 h-16 rounded-full flex items-center justify-center border border-green-200" data-id="element-1616">
                  <span className="text-sm font-bold" data-id="element-1617">Tea</span>
                </div>
                <div className="absolute bottom-0 left-0 bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center border border-amber-200" data-id="element-1618">
                  <span className="text-sm font-bold" data-id="element-1619">Coffee</span>
                </div>
                <div className="absolute bottom-0 right-0 bg-red-100 w-16 h-16 rounded-full flex items-center justify-center border border-red-200 shadow-md" data-id="element-1620">
                  <span className="text-sm font-bold" data-id="element-1621">Spora</span>
                </div>
                <div className="absolute inset-0 border-2 border-dashed border-gray-200 opacity-50" style={{
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
              }} data-id="element-1622"></div>
              </div>
            </div>
          </div>
          {/* Final Callout - with stronger ending */}
          <div className="bg-gray-50 p-8 rounded-xl mb-12 shadow-sm" data-id="element-1623">
            <h2 className="text-2xl font-bold mb-4 text-center" data-id="element-1624">
              The Third Liquid Culture Has Arrived
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center" data-id="element-1625">
              <p className="text-lg" data-id="element-1626">
                <span className="font-medium" data-id="element-1627">Coffee</span> is beans.
              </p>
              <p className="text-lg" data-id="element-1628">
                <span className="font-medium" data-id="element-1629">Tea</span> is leaves.
              </p>
              <p className="text-lg" data-id="element-1630">
                <span className="font-medium" data-id="element-1631">Spora</span> is fungi.
              </p>
            </div>
            <p className="text-lg text-center mb-6" data-id="element-1632">
              Together, they are the three great liquid cultures. But only Spora
              unlocks the full spectrum of compounds the body truly needs.
            </p>
            <p className="text-xl font-bold text-center" data-id="element-1633">
              The Third Liquid Culture™
            </p>
          </div>
          {/* Updated CTA */}
          <div className="flex justify-center" data-id="element-1634">
            <Link to="/how-it-works" className="bg-black text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors inline-flex items-center" data-id="element-1635">
              <span data-id="element-1636">Experience the Third Liquid Culture</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1637" />
            </Link>
          </div>
        </div>
      </div>
    </div>;
};
export default ThirdLiquidCulture;